import { useMemo } from "react"
import { AxisValue, DashBoardConditions } from "features/dashBoard/types"
import { CommonGraphStyle, filter, selectedItemStyle } from "../../common"
import { useMultiPieGraphOption } from "./options"
import { DownLoadData, DownLoadHeader, DownLoadRecord } from "components/download/csv/types"

interface SeriesData {
  code: string
  name: string
}
interface PieData {
  code: string
  name: string
  value: number
  series: { [index: string]: number }
  itemStyle?: CommonGraphStyle
}

type useMultiPieGraphModelProps<T> = {
	dataList: T[] | null
	conditions: DashBoardConditions,
	filterText: string | undefined
	graphOption: ReturnType<typeof useMultiPieGraphOption<T>>
}
export const useMultiPieGraphModel = <T,>({
	dataList,
	conditions,
	filterText,
	graphOption: {
		configure, targetList,
		axisDef, axis2Def, valueDef,
		sort, row
	}
}: useMultiPieGraphModelProps<T>) => useMemo(
	() => {
		if (!dataList || dataList.length === 0) return null
		if (!axisDef?.key || !valueDef?.key) return null

		const condition = axisDef.key ? conditions[axisDef.key] : undefined

		const seriesData: SeriesData[] = []
		const data: PieData[] = []
		const addRecord = (
			axisList: AxisValue[],
			seriesCode: string,
			value: number
		) => {
			for (const ax of axisList) {
				if (filterText && ax.name) {
					const axName = ax.name
					if (filterText.split(' ').every((t) => axName.indexOf(t) < 0)) continue
				}
				let it = data.find((r) => r.code === ax.code)
				if (!it) {
					it = {
						code: ax.code,
						name: ax.name || '',
						value: value,
						series: {},
						itemStyle: selectedItemStyle(condition, ax)
					}
					data.push(it)
				} else {
					it.value = it.value + value
				}
				it.series[seriesCode] = (it.series[seriesCode] ?? 0) + value
			}
		}

		for (const record of dataList) {
			const axis = axisDef.val(record)
			if (!axis) continue
			const value = valueDef.val(record)
			if (value === undefined || value === 0) continue
			if (!filter(record, configure, conditions, axisDef.key ?? '', targetList)) continue

			const axisList = Array.isArray(axis) ? axis : [axis]
			if (axis2Def) {
				const axis2 = axis2Def.val(record)
				if (!axis2) {
					if (!seriesData.find((r) => r.code === '')) {
						seriesData.push({ code: '', name: '' })
					}
					addRecord(axisList, '', value)
				} else {
					const axis2List = Array.isArray(axis2) ? axis2 : [axis2]
					for (const ax2 of axis2List) {
						if (!seriesData.find((r) => r.code === ax2.code)) {
							seriesData.push({ code: ax2.code, name: ax2.name || '' })
						}
						addRecord(axisList, ax2.code, value)
					}
				}
			} else {
				addRecord(axisList, '', value)
			}
		}
		data.sort((a, b) => sort ? (a.value - b.value) : a.code.localeCompare(b.code))
		seriesData.sort((a, b) => a.code.localeCompare(b.code))

		const radius = seriesData.length ? (85 / row) : 85
		return {
			series: seriesData,
			data,
			radius,
			csvBuilder: (): DownLoadData | undefined => {
				if (!axisDef?.key || !valueDef?.key) return undefined
				const axisKey = axisDef.key
				const valueKey = valueDef.key
				const header: DownLoadHeader[] = [
					{ title: axisDef.title, key: axisDef.key },
					{ title: valueDef.title, key: valueDef.key, unit: valueDef.unit }
				]
				header.push(...seriesData.map((series) => ({
					title: series.name,
					key: 'series_' + series.code,
					unit: valueDef.unit
				})))
				const body = data.map((r) => {
					const row: DownLoadRecord = {}
					row[axisKey] = r.name
					row[valueKey] = r.value
					seriesData.forEach((series) => {
						row['series_' + series.code] = r.series[series.code]
					})
					return row
				})
				return { header, body }
			}
		}
	},
	[dataList, axisDef, axis2Def, valueDef, sort, row, filterText, configure, conditions, targetList]
)
